<template>
  <div class="form-price" :class="[{ preview: preview }, ...wrapperClasses]">
    <div class="title flex mb-2">
      {{ title }}<span v-if="mandatory" class="required ml-2">*</span>
      <div
        v-if="!preview"
        @click="onClickHelp"
        class="flex cursor-pointer ml-2"
      >
        <feather-icon
          :icon="'HelpCircleIcon'"
          class="inline-flex rounded-full"
          svgClasses="h-5"
        ></feather-icon>
      </div>
    </div>
    <div v-if="showPlainText" class="flex">
      <div class="editable-input-area" @click="isEdit = true">
        <span class="editable-input-text">{{
          inputValue ? displayInputValue : "-"
        }}</span>
      </div>
      <common-updated-label
        :updatedAt="updateDateLabel"
        :createdAt="createDateLabel"
      />
      <div class="mandatory-switch flex" v-if="showMandatorySwitch">
        <vs-switch color="#2D9CDB" class="ml-6" v-model="mandatoryValue" />
        <span class="ml-2">必須</span>
      </div>
      <template v-if="showStatus">
        <status-chip
          v-if="isStatusFixed"
          class="ml-3 mr-2"
          :label="status.label"
          :color="status.color"
        ></status-chip>
        <status-dropdown-select
          v-else
          class="ml-3 mr-2"
          :options="filteredStatusOptions"
          :value.sync="status"
          :onClickOption="onStatusChange"
          clickEvent
        ></status-dropdown-select>
      </template>
    </div>
    <div v-else class="flex row-gap-8 col-gap-8 w-full">
      <hull-dropdown-select
        :selectedValue.sync="editSelectValue"
        :selectOptions="currencyOptions"
        :placeholder="selectPlaceholder"
      ></hull-dropdown-select>
      <vs-input
        class="w-full"
        v-model="editInputValue"
        :placeholder="textPlaceholder"
        :danger="!isEditInputValueValid"
        danger-text="数字のみ入力してください"
      />
      <div class="btn-area flex gap-3" v-if="!preview">
        <common-button
          class="save-btn-template"
          title="保存"
          type="filled"
          :click="
            () => {
              onClickSaveButton();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
          }"
          :disabled="preview || !inputAllValid"
        ></common-button>
        <common-button
          class="cancel-btn-template"
          type="border"
          color="#BDBDBD"
          :click="
            () => {
              cancelEdit();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
            fontSize: '12px',
          }"
          text-color="#757575"
          title="キャンセル"
          :disabled="preview"
        ></common-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ColorMixin } from "@/mixins";
import CommonButton from "../molecules/CommonButton.vue";
import HullDropdownSelect from "../molecules/HullDropdownSelect.vue";
import { toYMDHm_jp } from "@/utils/entity/DateUtil";
import ValidationUtil from "@/utils/entity/ValidationUtil";
import { HULL_INSURANCE_CURRENCY } from "@/config/const";
import CommonUpdatedLabel from "../molecules/CommonUpdatedLabel.vue";
import StatusDropdownSelect from "../organisms/StatusDropdownSelect.vue";
import StatusChip from "../molecules/StatusChip.vue";

export default {
  components: {
    CommonButton,
    HullDropdownSelect,
    CommonUpdatedLabel,
    StatusDropdownSelect,
    StatusChip,
  },
  mixins: [ColorMixin],
  name: "O.ClaimTypeTextSmall",
  props: {
    wrapperClasses: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    preview: {
      type: Boolean,
      default: false,
    },
    onClickSave: {
      type: Function,
      default: () => {},
    },
    onSwitchChange: {
      type: Function,
      default: () => {},
    },
    onClickHelp: {
      type: Function,
      default: () => {},
    },
    onStatusChange: {
      type: Function,
      default: () => {},
    },
    title: {
      type: String,
    },
    inputValue: {
      type: Object,
    },
    mandatory: {
      type: Boolean,
    },
    isAdmin: {
      type: Boolean,
    },
    createDate: {
      type: Number,
    },
    updateDate: {
      type: Number,
    },
    initialSwitchValue: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    initialStatusValue: {
      type: Object,
    },
    selectPlaceholder: {
      type: String,
    },
    textPlaceholder: {
      type: String,
    },
  },
  data() {
    return {
      isEdit: false,
      mandatoryValue: this.initialSwitchValue,
      editInputValue: null,
      editSelectValue: null,
      status: null,
    };
  },
  watch: {
    mandatoryValue(value) {
      this.onSwitchChange(value);
    },
  },
  created() {
    this.mandatoryValue = this.initialSwitchValue;
    this.initializeEditInput();
  },
  computed: {
    isEditInputValueValid() {
      if (this.preview) return true;

      return (
        this.editInputValue == "" ||
        ValidationUtil.isAllDigits(this.editInputValue)
      );
    },
    inputAllValid() {
      return this.isEditInputValueValid;
    },
    displayInputValue() {
      if (this.inputValue && this.inputValue.value) {
        return `${Number(this.inputValue.value).toLocaleString()} ${
          this.getInputValueCurrencyLabel
        }`;
      } else {
        return "-";
      }
    },
    getInputValueCurrencyLabel() {
      if (this.inputValue) {
        return HULL_INSURANCE_CURRENCY.find(
          element => element.id == this.inputValue.unit
        ).label;
      } else {
        return "";
      }
    },
    currencyOptions() {
      return HULL_INSURANCE_CURRENCY.map(element => {
        return { text: element.label, value: element.id };
      });
    },
    createDateLabel() {
      return toYMDHm_jp(this.createDate);
    },
    updateDateLabel() {
      return toYMDHm_jp(this.updateDate);
    },
    showPlainText() {
      if (!this.preview && !this.isEdit) {
        return true;
      } else {
        return false;
      }
    },
    showMandatorySwitch() {
      if (this.isAdmin && this.showPlainText) {
        return true;
      } else {
        return false;
      }
    },
    filteredStatusOptions() {
      return this.statusOptions.filter(element => element.value != 1);
    },
    isStatusFixed() {
      return this.status.value == 1 || !this.isAdmin;
    },
    showStatus() {
      return !this.preview && this.mandatory;
    },
  },

  methods: {
    initializeEditInput() {
      this.editInputValue = this.inputValue ? this.inputValue.value : "";
      this.editSelectValue = this.inputValue
        ? this.inputValue.unit
        : this.currencyOptions[0].value;
      this.status = this.initialStatusValue;
    },
    cancelEdit() {
      this.isEdit = false;
      this.initializeEditInput();
    },
    onClickSaveButton() {
      this.isEdit = false;
      this.onClickSave({
        value: this.editInputValue,
        unit: this.editSelectValue,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";
.form-price {
  min-height: 80px;
  width: 100%;
  &.preview {
    pointer-events: none;
  }
  .required {
    font-weight: bold;
    color: $aisea-red;
  }
  .update-date-label {
    font-size: 12px;
  }
  .editable-input-area {
    margin-right: 18px;
    transition: background-color 0.3s;
    flex: 1;
    cursor: pointer;
    .editable-input-text {
      line-height: 2.2;
    }
    &:hover {
      background: $hover-background-blue;
    }
  }
}
</style>
